import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SearchConsole from "@/components/SearchConsole";

const mask = (text, delay, extra = null) => (
  <span key={text} className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
      {extra}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pb-20 pt-28 md:pb-28 md:pt-40">
      <motion.div
        aria-hidden="true"
        style={{ y: bgY }}
        className="pointer-events-none absolute -right-40 -top-24 hidden w-[52rem] select-none lg:block"
      >
        <img
          src="https://images.unsplash.com/photo-1741806914412-340ca16e9175?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZSUyMHdoaXRlfGVufDB8fHx8MTc5MTMxODI3MXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full rounded-lg opacity-50 [mask-image:radial-gradient(closest-side,black,transparent)]"
        />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-8 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-crimson"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-crimson" />
            NYSCEF · New York State Courts
          </motion.p>
          <h1
            className="font-display text-[2.6rem] leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-[4.3rem]"
            data-testid="hero-headline"
          >
            {mask("Every case against", 0.15)}
            {mask("your client.", 0.28)}
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block italic text-crimson"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.95, delay: 0.41, ease: [0.22, 1, 0.36, 1] }}
              >
                One search away.
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-7 max-w-md text-base leading-relaxed text-smoke"
            data-testid="hero-subline"
          >
            DocketScout opens the official New York courts case search with your
            details ready to paste — check any client, merchant or borrower for
            MCA, consumer-credit and civil cases statewide.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-bold uppercase tracking-[0.16em] text-smoke"
            data-testid="hero-trust-row"
          >
            <span>Official NYSCEF search</span>
            <span className="h-1 w-1 rounded-full bg-crimson" />
            <span>All 62 counties</span>
            <span className="h-1 w-1 rounded-full bg-crimson" />
            <span>Nothing stored</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <SearchConsole />
          <motion.div
            animate={{ y: [0, -7, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-8 -left-3 hidden -rotate-2 rounded-md border border-hairline bg-paper px-4 py-3 shadow-[0_16px_40px_-16px_rgba(17,17,17,0.25)] sm:block lg:-left-10"
            data-testid="hero-floating-chip"
          >
            <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-smoke">Example docket</p>
            <p className="mt-1 flex items-center gap-2 font-display text-sm text-ink">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-crimson" />
              Consumer Credit — Kings County
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
